import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

function chip(active, label, danger = false, key) {
  if (!active) return null;
  return (
    <span key={key} className={`text-[9px] font-body font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
      danger ? 'bg-[#E63946] text-white' : 'bg-kawaii-peach/50 text-kawaii-textSoft'
    }`}>{label}</span>
  );
}

export default function CheckinTimeline({ episodeId, refreshToken }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/cellulitis/${episodeId}/checkins`)
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [episodeId, refreshToken]);

  if (loading) {
    return (
      <div className="mt-3 flex items-center gap-2 text-kawaii-textSoft text-xs font-body">
        <Loader2 className="w-3 h-3 animate-spin" /> loading check-ins...
      </div>
    );
  }
  if (!items.length) {
    return (
      <p className="mt-3 text-xs text-kawaii-textSoft italic font-body">
        No check-ins yet. Add one to start a timeline you can share with your doctor.
      </p>
    );
  }

  return (
    <div className="mt-3" data-testid={`checkin-timeline-${episodeId}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-kawaii-textSoft font-bold mb-2">Timeline</div>
      <div className="relative pl-4">
        <div className="absolute left-1 top-1 bottom-1 w-px bg-kawaii-border" />
        <div className="space-y-2">
          {items.map((c) => {
            const urgent = c.has_temperature || c.feels_feverish || c.red_streaks;
            return (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                className="relative"
                data-testid={`checkin-${c.id}`}
              >
                <span className={`absolute -left-3 top-2 w-2.5 h-2.5 rounded-full border-2 border-white ${urgent ? 'bg-[#E63946]' : 'bg-kawaii-mint'}`} />
                <div className="rounded-xl bg-white border border-kawaii-border px-3 py-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-heading font-semibold text-kawaii-text">
                      {format(parseISO(c.date), "EEE, MMM d")}
                    </span>
                    {c.temperature_c && (
                      <span className="text-[10px] font-body text-kawaii-textSoft">🌡️ {c.temperature_c}°</span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {chip(c.has_temperature, "Temp", true, "t")}
                    {chip(c.feels_feverish, "Feverish", true, "f")}
                    {chip(c.red_streaks, "Streaks", true, "r")}
                    {chip(c.feels_achy, "Achy", false, "a")}
                    {chip(c.warm_to_touch, "Warm", false, "w")}
                    {chip(c.skin_breaks, "Breaks", false, "b")}
                    {!c.has_temperature && !c.feels_feverish && !c.red_streaks && !c.feels_achy && !c.warm_to_touch && !c.skin_breaks && (
                      <span className="text-[9px] font-body font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-kawaii-mint/60 text-kawaii-text">All clear ♡</span>
                    )}
                  </div>
                  {c.notes && <p className="mt-1 text-[11px] text-kawaii-textSoft italic font-body">"{c.notes}"</p>}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
